import { Component, inject, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { ApiService, ConfigService } from '@app-core/services';
import { OverlayRef } from '@app-core/services/overlay';
import { EmailValidator, RequiredValidator } from '@app-core/validations';
import { IForgotUsernameRequest } from '@app-model/request';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { RecaptchaComponent } from '@app-shared/recaptcha/recaptcha.component';
import { untilDestroyed } from '@app-shared/utils';
import * as fromRoot from '@app-store/reducers';
import { throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { SpinnerComponent } from '../spinner/spinner.component';

interface IForgotUsernameForm {
  email: FormControl<string>;
  captcha: FormControl<string>;
}

@Component({
  templateUrl: './forgot-username-popup.component.html',
  styleUrls: ['./forgot-username-popup.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RecaptchaComponent,
    ButtonComponent,
    IconComponent,
    InputControlComponent,
    SpinnerComponent,
  ],
})
export class ForgotUsernamePopupComponent {
  private ref = inject<OverlayRef<ForgotUsernamePopupComponent>>(OverlayRef);
  private fb = inject(NonNullableFormBuilder);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private apiService = inject(ApiService);
  private _configService = inject(ConfigService);

  @ViewChild(RecaptchaComponent) recaptchaRef: RecaptchaComponent | undefined;

  usernameForm: FormGroup<IForgotUsernameForm>;
  emailSuccess = signal(false);
  processing = signal(false);

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    this.usernameForm = this.fb.group({
      email: this.fb.control('', [EmailValidator, RequiredValidator]),
      captcha: this.fb.control('', [RequiredValidator]),
    });
  }

  forgotUsername(): void {
    if (this.usernameForm.invalid) {
      return;
    }

    if (this._configService.environment.authorization.flow === 'okta') {
      this.store$
        .select((store) => store.configState)
        .pipe(
          take(1),
          switchMap((data) => {
            this.processing.set(true);
            return this.apiService.forgotOktaUserName({
              application: this._configService.appIdentifier,
              programId: data.clientConfig.programId,
              emailAddress: this.usernameForm.get('email').value,
              captcha: this.usernameForm.get('captcha').value,
              captchaVersion: 'v3',
            });
          }),
          catchError((err: Error) => {
            this.processing.set(false);
            this.recaptchaRef?.execute();

            return throwError(() => err);
          })
        )
        .subscribe(() => {
          this.emailSuccess.set(true);
          this.processing.set(false);
        });
    } else {
      this.store$
        .select((store) => store.configState)
        .pipe(
          take(1),
          switchMap((data) => {
            this.processing.set(true);
            const request: IForgotUsernameRequest = {
              application: this._configService.appIdentifier,
              programId: data.clientConfig.programId,
              emailAddress: this.usernameForm.get('email').value,
              captcha: this.usernameForm.get('captcha').value,
              captchaVersion: 'v3',
            };

            return this.apiService.forgotUsername(request);
          }),
          filter((data) => data?.status),
          catchError((err: Error) => {
            this.processing.set(false);
            this.usernameForm.reset();
            this.recaptchaRef?.execute();

            return throwError(() => err);
          }),
          this.#untilDestroyed()
        )
        .subscribe(() => {
          this.emailSuccess.set(true);
          this.processing.set(false);
          this.usernameForm.reset();
        });
    }
  }

  close(): void {
    this.ref.close();
  }

  onCaptchaResolved(token: string) {
    this.usernameForm.get('captcha')?.setValue(token);
  }
}
